import { gaiaAxios } from './api';

/**
 * GAIA Chat API service.
 *
 * Wraps the backend /gaia/* endpoints.  All functions return
 * Promises that resolve to the JSON response body.
 */

/**
 * Create a new GAIA chat session.
 * @returns {Promise<{sessionId: string}>}
 */
export function createSession() {
  return gaiaAxios.post('/gaia/sessions').then(r => r.data);
}

/**
 * Delete a GAIA chat session.
 * @param {string} sessionId
 * @returns {Promise<object>}
 */
export function deleteSession(sessionId) {
  return gaiaAxios.delete(`/gaia/sessions/${sessionId}`).then(r => r.data);
}

/**
 * Start a new interaction (send a user message).
 * @param {string} sessionId
 * @param {string} prompt
 * @param {object} [options]  Optional keys: attachments, chatHistory, toolState, actionChoice, configurations
 * @returns {Promise<{interactionId: string, status: string}>}
 */
export function startInteraction(sessionId, prompt, options = {}) {
  const body = {
    prompt,
    ...(options.attachments && { attachments: options.attachments }),
    ...(options.chatHistory && { chatHistory: options.chatHistory }),
    ...(options.toolState && { toolState: options.toolState }),
    ...(options.actionChoice && { actionChoice: options.actionChoice }),
    ...(options.configurations && { configurations: options.configurations }),
  };
  return gaiaAxios.post(`/gaia/sessions/${sessionId}/interactions`, body).then(r => r.data);
}

/**
 * Poll an interaction's status.
 * @param {string} sessionId
 * @param {string} interactionId
 * @returns {Promise<object>}
 */
export function getInteraction(sessionId, interactionId) {
  return gaiaAxios.get(`/gaia/sessions/${sessionId}/interactions/${interactionId}`).then(r => r.data);
}

const POLL_INTERVAL_MS = 1500;
const MAX_IDLE_ATTEMPTS = 40; // give up after ~1 min with no new text

/**
 * Poll an existing interaction until it completes.
 *
 * @param {string} sessionId
 * @param {string} interactionId
 * @param {AbortSignal} [signal]  Optional AbortSignal to cancel polling
 * @param {function} [onProgress]  Called with partial text on each in_progress poll
 * @returns {Promise<object>}  The final interaction result
 */
export async function pollInteraction(sessionId, interactionId, signal = null, onProgress = null) {
  let idleAttempts = 0;
  let lastTextLen = 0;

  while (idleAttempts < MAX_IDLE_ATTEMPTS) {
    if (signal?.aborted) throw new DOMException('Polling aborted', 'AbortError');
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

    const result = await getInteraction(sessionId, interactionId);

    // Surface partial text while still in progress
    if (result.status === 'in_progress' && onProgress) {
      const msgs = result.newAiMessages || [];
      const last = [...msgs].reverse().find(m => m.body?.text);
      if (last?.body?.text) {
        onProgress(last.body.text);
        // Reset idle counter whenever new content arrives
        if (last.body.text.length !== lastTextLen) {
          lastTextLen = last.body.text.length;
          idleAttempts = 0;
        }
      }
    }

    if (result.status === 'finished_successfully' || result.status === 'finished_with_error') {
      return result;
    }
    // Any other status (e.g. "in_progress") → keep polling
    idleAttempts++;
  }

  throw new Error('GAIA interaction timed out after polling');
}

/**
 * Start an interaction and poll until it completes.
 *
 * @param {string} sessionId
 * @param {string} prompt
 * @param {object} [options]  Same optional keys as startInteraction
 * @param {AbortSignal} [signal]  Optional AbortSignal to cancel polling
 * @param {function} [onProgress]  Called with partial text on each in_progress poll
 * @returns {Promise<object>}  The final interaction result
 */
export async function sendMessageAndPoll(sessionId, prompt, options = {}, signal = null, onProgress = null) {
  const startResult = await startInteraction(sessionId, prompt, options);
  const { interactionId } = startResult;

  const result = await pollInteraction(sessionId, interactionId, signal, onProgress);

  // Merge chatHistory from the start_interaction response into the polling
  // result.  GAIA returns the updated conversation history only in the
  // start_interaction response, not in the polling response.
  if (startResult.chatHistory && !result.chatHistory) {
    result.chatHistory = startResult.chatHistory;
  }
  return result;
}
